import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AppButton } from '../../button/app-button';
import { Field, Input, Label } from '../../form-kit';
import { SegmentedControl } from '../../segmented-control/segmented-control';
import { palette } from '../../../style-utils/theme';
import {
  Canvas,
  FootHint,
  Hint,
  PAD_HEIGHT,
  PAD_MAX_WIDTH,
  PadFoot,
  PadFrame,
  Panel,
  Root,
  TypedPreview,
} from './signature-pad.styled';

/**
 * A SIGNATURE, TYPED OR DRAWN — the house-rules signature (plan S6).
 *
 * Two ways to sign, one value:
 *   typed  {kind:'typed', name}   the form-kit Input (autocomplete name),
 *          prefilled with `name`, previewed in an italic of the UI face;
 *   drawn  {kind:'drawn', dataUrl, strokes}  a canvas, pointer events, the
 *          PNG trimmed to the ink + 8px and never larger than 1200 x 400.
 * `null` whenever the chosen way holds no signature: an empty name, or a
 * drawing of fewer than 2 strokes AND under 60 css px of path (a stray tap is
 * not a signature). The visible `labels.empty` line says so.
 *
 * Typed is the default for a mouse or a keyboard (no coarse pointer), drawn for
 * a finger; `defaultMode` overrides both. Keyboard and screen-reader users
 * always have the typed tab: the canvas is an image (role img, named by
 * `labels.drawHint`) and is never in the tab order.
 *
 * WebKit scrolls the page under a drawing finger unless the canvas has
 * touch-action none AND a non-passive touchmove listener that calls
 * preventDefault — both are here, on the canvas only.
 *
 * Switching tabs keeps both: the typed name and the strokes stay, and the tab
 * switched to emits its own value. No external library.
 */

export type SignatureValue =
  | { kind: 'typed'; name: string }
  | { kind: 'drawn'; dataUrl: string; strokes: number };

export type SignatureMode = 'typed' | 'drawn';

export interface SignaturePadLabels {
  typeTab: string;
  drawTab: string;
  /** The typed field's label, e.g. "Type your full name". */
  typeLabel: string;
  /** The canvas's accessible name and visible instruction. */
  drawHint: string;
  clear: string;
  /** Shown while the chosen way holds no signature. */
  empty: string;
  /** The Type / Draw group's accessible name. Default "typeTab / drawTab". */
  mode?: string;
}

export interface SignaturePadProps {
  value: SignatureValue | null;
  onChange: (value: SignatureValue | null) => void;
  labels: SignaturePadLabels;
  defaultMode?: SignatureMode;
  /** Prefill for the typed signature (the lead guest's name). */
  name: string;
  disabled?: boolean;
  className?: string;
}

type Point = { x: number; y: number };

/** A drawing counts with at least this many strokes … */
export const MIN_STROKES = 2;
/** … or at least this much path, in css px. */
export const MIN_PATH_LENGTH = 60;
/** Transparent margin around the ink in the exported PNG, css px. */
export const EXPORT_PADDING = 8;
export const EXPORT_MAX_WIDTH = 1200;
export const EXPORT_MAX_HEIGHT = 400;
const DPR_CAP = 2;
const INK_WIDTH = 2;
/** The signing line: 40px above the pad's foot, 16px in from each side. */
const BASELINE_FROM_BOTTOM = 40;
const BASELINE_INSET = 16;

const isCoarsePointer = (): boolean => {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches
    );
  } catch {
    return false;
  }
};

/** Whether a drawing is a signature: 2+ strokes, or 60+ css px of path. */
export const drawingCounts = (strokes: number, length: number): boolean =>
  strokes >= MIN_STROKES || length >= MIN_PATH_LENGTH;

/* jsdom (and a browser out of canvas memory) has no 2D context: draw nothing,
   and say nothing, rather than throw. */
const contextOf = (
  canvas: HTMLCanvasElement | null
): CanvasRenderingContext2D | null => {
  if (!canvas) return null;
  try {
    return canvas.getContext('2d');
  } catch {
    return null;
  }
};

const typedValue = (raw: string): SignatureValue | null => {
  const trimmed = raw.trim();

  return trimmed ? { kind: 'typed', name: trimmed } : null;
};

const inkStyle = (ctx: CanvasRenderingContext2D): void => {
  ctx.strokeStyle = palette.ink;
  ctx.fillStyle = palette.ink;
  ctx.lineWidth = INK_WIDTH;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
};

const drawStroke = (ctx: CanvasRenderingContext2D, points: Point[]): void => {
  if (points.length === 0) return;
  inkStyle(ctx);
  ctx.beginPath();
  if (points.length === 1) {
    ctx.arc(points[0].x, points[0].y, INK_WIDTH / 2, 0, Math.PI * 2);
    ctx.fill();

    return;
  }
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i].x, points[i].y);
  }
  ctx.stroke();
};

/**
 * The strokes as a PNG: cropped to the ink's bounding box plus EXPORT_PADDING,
 * drawn at the device ratio (cap 2) and scaled DOWN to fit 1200 x 400. Drawn
 * again on an offscreen canvas rather than copied, so the signing line on the
 * visible pad never reaches the document.
 */
export const exportStrokes = (
  strokes: ReadonlyArray<Point[]>,
  ratio: number
): string | null => {
  const points = strokes.flat();
  if (points.length === 0 || typeof document === 'undefined') return null;
  const margin = INK_WIDTH / 2 + EXPORT_PADDING;
  const minX = Math.min(...points.map(p => p.x)) - margin;
  const minY = Math.min(...points.map(p => p.y)) - margin;
  const maxX = Math.max(...points.map(p => p.x)) + margin;
  const maxY = Math.max(...points.map(p => p.y)) + margin;
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const scale = Math.min(
    ratio,
    EXPORT_MAX_WIDTH / width,
    EXPORT_MAX_HEIGHT / height
  );
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.min(EXPORT_MAX_WIDTH, Math.round(width * scale)));
  out.height = Math.max(
    1,
    Math.min(EXPORT_MAX_HEIGHT, Math.round(height * scale))
  );
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.setTransform(scale, 0, 0, scale, -minX * scale, -minY * scale);
  strokes.forEach(stroke => drawStroke(ctx, stroke));

  return out.toDataURL('image/png');
};

export const SignaturePad: React.FC<SignaturePadProps> = ({
  value,
  onChange,
  labels,
  defaultMode,
  name,
  disabled = false,
  className,
}) => {
  const uid = useId();
  const inputId = `${uid}-typed`;
  const hintId = `${uid}-hint`;

  const [mode, setMode] = useState<SignatureMode>(
    () => defaultMode ?? value?.kind ?? (isCoarsePointer() ? 'drawn' : 'typed')
  );
  const [typedName, setTypedName] = useState<string>(() =>
    value?.kind === 'typed' ? value.name : name
  );
  /* What the strokes add up to, as state so the hint re-renders; the points
     themselves live in refs and never cause a render while drawing. */
  const [ink, setInk] = useState({ strokes: 0, length: 0 });

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokesRef = useRef<Point[][]>([]);
  const lengthRef = useRef(0);
  const currentRef = useRef<Point[] | null>(null);
  const pointerRef = useRef<number | null>(null);
  const sizeRef = useRef({ width: PAD_MAX_WIDTH, ratio: 1 });

  const drawnValue = useCallback((): SignatureValue | null => {
    const strokes = strokesRef.current.length;
    if (!drawingCounts(strokes, lengthRef.current)) return null;
    const dataUrl = exportStrokes(strokesRef.current, sizeRef.current.ratio);

    return dataUrl ? { kind: 'drawn', dataUrl, strokes } : null;
  }, []);

  /* The prefilled name IS a typed signature: say so once, on mount, when the
     caller has none yet, so what is shown and what is held agree. */
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    if (mode !== 'typed' || value !== null) return;
    const next = typedValue(typedName);
    if (next) onChangeRef.current(next);
  }, [mode, value, typedName]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = contextOf(canvas);
    if (!canvas || !ctx) return;
    const { width, ratio } = sizeRef.current;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, width, PAD_HEIGHT);
    const baseline = PAD_HEIGHT - BASELINE_FROM_BOTTOM;
    ctx.beginPath();
    ctx.strokeStyle = palette.line;
    ctx.lineWidth = 1;
    ctx.moveTo(BASELINE_INSET, baseline);
    ctx.lineTo(Math.max(BASELINE_INSET, width - BASELINE_INSET), baseline);
    ctx.stroke();
    strokesRef.current.forEach(stroke => drawStroke(ctx, stroke));
  }, []);

  /* Size the backing store to the pad (device ratio, cap 2), keep it sized,
     and stop WebKit scrolling under a drawing finger. */
  useEffect(() => {
    if (mode !== 'drawn') return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const layout = () => {
      const measured =
        canvas.getBoundingClientRect().width || canvas.clientWidth;
      const width = measured > 0 ? measured : PAD_MAX_WIDTH;
      const ratio = Math.min(
        DPR_CAP,
        Math.max(1, (typeof window !== 'undefined' && window.devicePixelRatio) || 1)
      );
      if (
        sizeRef.current.width === width &&
        sizeRef.current.ratio === ratio &&
        canvas.width === Math.round(width * ratio)
      ) {
        return;
      }
      sizeRef.current = { width, ratio };
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(PAD_HEIGHT * ratio);
      redraw();
    };

    sizeRef.current = { width: 0, ratio: 0 };
    layout();

    const blockScroll = (event: TouchEvent) => {
      if (event.cancelable) event.preventDefault();
    };
    canvas.addEventListener('touchmove', blockScroll, { passive: false });

    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(layout) : null;
    observer?.observe(canvas);

    return () => {
      canvas.removeEventListener('touchmove', blockScroll);
      observer?.disconnect();
    };
  }, [mode, redraw]);

  const pointOf = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();

    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    const canvas = event.currentTarget;
    try {
      canvas.setPointerCapture?.(event.pointerId);
    } catch {
      /* a pointer that already ended cannot be captured; drawing still works */
    }
    const point = pointOf(event);
    pointerRef.current = event.pointerId;
    currentRef.current = [point];
    const ctx = contextOf(canvas);
    if (ctx) drawStroke(ctx, [point]);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = currentRef.current;
    if (!stroke || event.pointerId !== pointerRef.current) return;
    const point = pointOf(event);
    const last = stroke[stroke.length - 1];
    const step = Math.hypot(point.x - last.x, point.y - last.y);
    if (step < 0.5) return;
    stroke.push(point);
    lengthRef.current += step;
    const ctx = contextOf(event.currentTarget);
    if (ctx) drawStroke(ctx, [last, point]);
  };

  const endStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = currentRef.current;
    if (!stroke || event.pointerId !== pointerRef.current) return;
    currentRef.current = null;
    pointerRef.current = null;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      /* already released */
    }
    strokesRef.current = [...strokesRef.current, stroke];
    setInk({ strokes: strokesRef.current.length, length: lengthRef.current });
    onChange(drawnValue());
  };

  const clear = () => {
    strokesRef.current = [];
    lengthRef.current = 0;
    currentRef.current = null;
    pointerRef.current = null;
    setInk({ strokes: 0, length: 0 });
    redraw();
    onChange(null);
  };

  const selectMode = (next: SignatureMode) => {
    if (disabled || next === mode) return;
    setMode(next);
    onChange(next === 'typed' ? typedValue(typedName) : drawnValue());
  };

  const onTyped = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setTypedName(raw);
    onChange(typedValue(raw));
  };

  const missing =
    mode === 'typed'
      ? typedValue(typedName) === null
      : !drawingCounts(ink.strokes, ink.length);

  return (
    <Root className={className} data-signature-mode={mode}>
      <SegmentedControl<SignatureMode>
        options={[
          { value: 'typed', label: labels.typeTab },
          { value: 'drawn', label: labels.drawTab },
        ]}
        value={mode}
        onChange={selectMode}
        label={labels.mode ?? `${labels.typeTab} / ${labels.drawTab}`}
        disabled={disabled}
      />

      {mode === 'typed' ? (
        <Panel>
          <Field>
            <Label htmlFor={inputId}>{labels.typeLabel}</Label>
            <Input
              id={inputId}
              type="text"
              autoComplete="name"
              autoCapitalize="words"
              spellCheck={false}
              value={typedName}
              onChange={onTyped}
              disabled={disabled}
              aria-describedby={missing ? hintId : undefined}
            />
          </Field>
          <TypedPreview aria-hidden="true" data-signature-preview="">
            {typedName.trim()}
          </TypedPreview>
        </Panel>
      ) : (
        <Panel>
          {/* The instruction, for the eye; the canvas carries it as its name. */}
          <Hint aria-hidden="true">{labels.drawHint}</Hint>
          <PadFrame $disabled={disabled}>
            <Canvas
              ref={canvasRef}
              role="img"
              aria-label={labels.drawHint}
              $disabled={disabled}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endStroke}
              onPointerCancel={endStroke}
            />
          </PadFrame>
        </Panel>
      )}

      <PadFoot>
        <FootHint id={hintId} aria-live="polite">
          {missing ? labels.empty : ''}
        </FootHint>
        {mode === 'drawn' ? (
          <AppButton
            variant="ghost"
            onClick={clear}
            disabled={disabled}
            data-signature-clear=""
          >
            {labels.clear}
          </AppButton>
        ) : null}
      </PadFoot>
    </Root>
  );
};
